import { Injectable } from '@angular/core';
import { NgbModal }  from '@ng-bootstrap/ng-bootstrap';

import { ApiService }            from '../api/api.service';
import { CompanyService }        from '../companies/company.service';
import { SessionCompanyModel }   from '../sessions/models/session.company';
import { TableService }          from '../table/table.service';
import { VendorModel }           from './models/vendor';
import { VendorPaymentsService } from '../vendor-payments/vendor-payments.service';

import { VendorChangeComponent }                from './vendor-change.component';
import { VendorInvoicesForPaymentComponent }    from '../vendor-invoices/vendor-invoices.for-payment.component';
import { VendorLinkedVendorsComponent }         from './vendor-linked-vendors.component';
import { VendorManuallyLinkComponent }          from './vendor-manually-link.component';
import { VendorQuickAddComponent }              from './vendor.quick-add.component';
import { VendorResolveMatchesComponent }        from './vendor-resolve-matches.component';

@Injectable({ providedIn: 'root' })
export class VendorFactoryService {
  vendor: VendorModel;

  constructor (
    private apiService: ApiService,
    private companyService: CompanyService,
    private tableService: TableService,
    private vendorPaymentsService: VendorPaymentsService,
    private ngbModal: NgbModal
  ) {}

  init(vendor?: VendorModel): VendorFactoryService {


    this.vendor = vendor || {};


    return this;


  }

  change (): Promise<any> {
    const modalRef = this.ngbModal.open(VendorChangeComponent, { keyboard: false });

    modalRef.componentInstance.vendor = this.vendor;

    return modalRef.result;
  }

  deleteLinkedVendor(vendor: VendorModel): Promise<any> {


    return this.apiService.get('/vendors/destroy_branch_link', [
      this.vendor.id, vendor.company_id, vendor.id
    ]);


  }

  findMatchingVendors(vendor: VendorModel): Promise<any> {


    return this.apiService.get('/branch_matching/find_matches_for_vendor', vendor.id);


  }

  getBiller (): Promise<any> {


    return this.apiService.get('/vendor', this.vendor.biller_id);


  }

  getBranchVendorInvoices(params: any = {}): Promise<any> {


    return this.vendorPaymentsService.getInvoicesForPayment(this.vendor, params, true);


  }

  getLinkedVendors (): Promise<any> {


    return this.apiService.get('/vendors/branch_link_index', this.vendor.id);


  }

  isBranchVendor(vendor = this.vendor): boolean {


    return vendor?.is_branch_vendor;


  }

  isLinkedToHeadOffice(vendor = this.vendor): boolean {


    return !!vendor?.branch_link;


  }

  lookupBranchVendorInvoices (): Promise<any> {
    const tableCacheKey = 'branchVendorInvoicesForPayment';
    const cache         = this.tableService.getCacheForTable(tableCacheKey);

    return this.getBranchVendorInvoices({
      direction: cache.sorting.direction,
      field    : cache.sorting.field
    }).then(invoices => {
      const modalRef = this.ngbModal.open(VendorInvoicesForPaymentComponent, { windowClass: 'x-lg-modal' });

      modalRef.componentInstance.invoices = invoices;
      modalRef.componentInstance.settings = { isBranchPayment: true, tableCacheKey };
      modalRef.componentInstance.vendor   = this.vendor;

      return modalRef.result;
    });
  }

  manuallyLinkVendor (): Promise<any> {
    return this.apiService.get('/branches/linked_companies').then(company => {
      const modalRef = this.ngbModal.open(VendorManuallyLinkComponent, { windowClass: 'x-lg-modal' });

      modalRef.componentInstance.company = company;

      return modalRef.result;
    });
  }

  resolveMatches(vendor: VendorModel, matches: any): Promise<any> {
    const modalRef = this.ngbModal.open(VendorResolveMatchesComponent, { keyboard: false, windowClass: 'x-lg-modal' });

    modalRef.componentInstance.matches = matches;

    return modalRef.result.catch(() => vendor);
  }

  save(settings: any = {}): Promise<any> {
    const method = this.vendor.id ? 'patch' : 'post';
    const url    = this.vendor.id ? '/vendor' : '/vendors';

    return this.apiService[method](url, this.vendor, 'vendor', null, settings)
      .then(vendor => {
        if (!this.vendor.id
          && this.companyService.isSharedAccounting()
          && this.companyService.isAccountingSetup()
        ) {
          return this.findMatchingVendors(vendor)
            .then(matches => matches.length
              ? this.resolveMatches(vendor, matches).then(() => vendor)
              : vendor
            );
        }
        return vendor;
      });
  }

  saveLinkedVendor(company: SessionCompanyModel, linkedVendor: VendorModel): Promise<any> {


    return this.apiService.get('/vendors/link_to_branch', [
      this.vendor.id, company.company_id, linkedVendor.id
    ]);


  }

  saveMatch(match: any): Promise<any> {
    return this.apiService.post('/branch_matching/link_vendor_ids', [
      { id: this.vendor.id, accounting_id: match.accounting_id }
    ], 'links');
  }

  searchVendorsInCompany(company: SessionCompanyModel, filters?: any): Promise<any> {
    const defaultFilters = {
      linked_company_id: company.company_id,
      search_term      : '',
      search_type      : 'starts_with'
    };

    return this.apiService.post('vendors/find_in_branch', { ...defaultFilters, ...filters });
  }

  viewLinkedVendors (): Promise<any> {
    return this.getLinkedVendors().then(links => {
      const modalRef = this.ngbModal.open(VendorLinkedVendorsComponent, { keyboard: false, windowClass: 'lg-modal' });

      modalRef.componentInstance.links = links;

      return modalRef.result;
    });
  }

  get(id?: string): Promise<any> {
    if (!id) {
      return Promise.resolve({
        address1      : '',
        address2      : '',
        balance       : '0.00',
        company_name  : '',
        contact1_first: '',
        contact2_first: '',
        contact1_last : '',
        contact2_last : '',
        contact1_phone: '',
        contact2_phone: '',
        country_code  : this.companyService.company?.country_code,
        credit_balance: 0.00,
        email         : '',
        fax           : '',
        id            : '',
        phone         : '',
        postcode      : '',
        state         : '',
        suburb        : '',
        web           : ''
      });
    }

    let vendor: any;
    return this.apiService.get('/vendor', id)
      .then(result => {
        vendor = result;
        return this.apiService.get('/vendor/biller_account_balance', id);
      })
      .then(balance => {
        vendor.biller_balance = balance;
        return this.apiService.get('/vendor/biller_credit_balance', id);
      })
      .then(creditBalance => {
        vendor.biller_credit_balance = creditBalance;
        return vendor;
      });
  }

  quickAdd (): Promise<any> {
    const modalRef = this.ngbModal.open(VendorQuickAddComponent, { windowClass: 'md-modal' });

    this.apiService.get('/system/state_types', null, 'states')
      .then(states => modalRef.componentInstance.states = states);

    return modalRef.result;
  }
}
